import { useEffect, useRef, useState, type ReactNode } from "react";

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Starting translateY offset in px (the element slides up into place). */
  y?: number;
  /** Stagger delay in ms (use index * N for sequential reveals). */
  delay?: number;
  /** Fraction of the element that must be visible before revealing. */
  threshold?: number;
}

/**
 * Scroll-reveal wrapper — fades + slides its children up as they enter the
 * viewport. The core of the "247 feel" (premium activewear sites reveal
 * editorial blocks on scroll rather than dumping everything at once).
 *
 * Robust-by-default: the element renders VISIBLE in SSR and on first paint, so
 * content is never blank for no-JS clients, crawlers, slow hydration, reduced-
 * motion, Safari, or a degenerate (zero-height) viewport. Only AFTER mount, and
 * only for elements that are genuinely below the fold, do we hide-then-reveal —
 * the hide happens off-screen so there's no visible flash, and an
 * IntersectionObserver (with a fail-safe timer) animates it in on scroll.
 *
 * Built on a tiny IntersectionObserver instead of framer-motion to keep the
 * storefront bundle lean (framer-motion is confined to the B2B portals).
 */
export function Reveal({ children, className = "", y = 28, delay = 0, threshold = 0.12 }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Default visible — never hide content before we KNOW we can animate it back.
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vh = window.innerHeight;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // Bail (stay visible, no animation) on any condition where hiding would be
    // unsafe: reduced motion, no IntersectionObserver, or a zero-height viewport.
    if (reduce || typeof IntersectionObserver === "undefined" || !vh) return;
    // Already in or above the fold at mount → leave visible (no animation; the
    // user is already looking at it). Only animate things below the fold.
    if (el.getBoundingClientRect().top < vh) return;

    setHidden(true); // safe: element is off-screen, no visible flash
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setHidden(false);
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            reveal();
            io.disconnect();
            break;
          }
        }
      },
      { threshold, rootMargin: "0px 0px -6% 0px" },
    );
    io.observe(el);
    // Fail-safe: never leave content stuck hidden if the observer never fires.
    const fallback = setTimeout(reveal, 1600);
    return () => {
      io.disconnect();
      clearTimeout(fallback);
    };
  }, [threshold]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: hidden ? 0 : 1,
        transform: hidden ? `translate3d(0, ${y}px, 0)` : "none",
        transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        willChange: hidden ? "opacity, transform" : "auto",
      }}
    >
      {children}
    </div>
  );
}
